import {
  Anchor, Box, Button, Checkbox, Group, Modal, Pagination, Select, SimpleGrid, Stack, Table,
  Tabs, Text, Textarea, TextInput
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@dehub/theme";
import { ArrowRightIcon, CheckCircleIcon, DownloadSimpleIcon, EyeIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { DamagedInventoryApi, DeliveryApi } from "../../apis";
import type { FinalDisposition, IBulkResponse } from "../../apis/damaged-inventory";
import type { InventoryImpactType, IReturnDamagedInventory } from "../../apis/return";
import { Dot, SelectionBar, TableFrame } from "../../components/delivery/atoms";
import type { Tone } from "../../components/delivery/atoms";
import { DeliveryActionButton, DeliveryRowAction } from "../../components/delivery/action-controls";
import { PageLayout } from "../../components/layout";
import { DetailCard } from "../../components/return/detail-card";
import { RETURN_BULK_MAX, useReturnActions } from "../../components/return/return-actions";
import { ListState } from "../../components/ui";
import classes from "../../components/ui/pill-tabs.module.css";
import { downloadBlob } from "../../utils/export-returns-csv";
import { dateFormat, dateTimeFormat } from "../../utils/date";
import { message } from "../../utils/message";
import { IAuth } from "../../store/Auth";

const PAGE_SIZE = 20;

// Lining figures for codes / dates.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

// Disposal-status filter → the `disposed` boolean param (undefined = all).
const DISPOSED_FILTER: { value: string; disposed?: boolean }[] = [
  { value: "ALL" },
  { value: "PENDING",  disposed: false },
  { value: "DISPOSED", disposed: true }
];

const disposedFilterOptions = (t: TFunction) =>
  DISPOSED_FILTER.map((d) => ({ value: d.value, label: t(`inbound:damaged.disposedFilter.${d.value}`) }));

export const BUCKET_TAB_META: { value: InventoryImpactType; tone: Tone }[] = [
  { value: "RESTOCK_GOOD",           tone: "green" },
  { value: "DAMAGED_SEPARATE_STOCK", tone: "amber" },
  { value: "DAMAGED_WRITE_OFF",      tone: "red" },
  { value: "EXPIRED_DESTROY",        tone: "grey" }
];

export const bucketLabel = (t: TFunction, code?: string | null): string =>
  code ? t(`inbound:damaged.bucket.${code}`, { defaultValue: "-" }) : "-";

export const bucketTone = (code?: string | null): Tone =>
  BUCKET_TAB_META.find((b) => b.value === code)?.tone ?? "grey";

const DISPOSITION_VALUES: FinalDisposition[] = [
  "DESTROYED",
  "RESOLD",
  "DONATED",
  "RETURNED_TO_SUPPLIER"
];

export const dispositionOptions = (t: TFunction): { value: FinalDisposition; label: string }[] =>
  DISPOSITION_VALUES.map((value) => ({ value, label: t(`inbound:damaged.disposition.${value}`) }));

export const dispositionLabel = (t: TFunction, code?: string | null): string =>
  code ? t(`inbound:damaged.disposition.${code}`, { defaultValue: "-" }) : "-";

interface DisposeTarget {
  id: string;
  bucket: InventoryImpactType;
}

type BulkModal = "dispose" | "move" | "sell-damaged" | null;

function showBulkResult(t: TFunction, result: IBulkResponse) {
  const color = result.failed === 0 ? "green" : result.succeeded === 0 ? "red" : "yellow";
  showNotification({
    color,
    title  : t("inbound:damaged.bulk.title"),
    message: t("inbound:damaged.bulk.result", { succeeded: result.succeeded, failed: result.failed })
  });
}

export function DamagedInventoryPage() {
  const { t } = useTranslation(["inbound", "common"]);
  const navigate = useNavigate();
  const { permissionState } = useReturnActions();
  // RETURN_SET_INVENTORY_IMPACT gates dispose, move-bucket and both bulk routes (BE damaged-inventory/*).
  const perm = permissionState("bulkDispose");
  const mutateState = { allowed: perm.enabled, reason: perm.reason };

  // O164 — the page opens on the first category that holds rows; until the counts load it stays on the first tab.
  const [bucketChoice, setBucketChoice] = useState<InventoryImpactType | null>(null);

  // ─── Filters + pagination ─────────────────────────────────────────────────
  const [page, setPage]               = useState(1);
  const [sku, setSku]                 = useState("");
  const [dateFrom, setDateFrom]       = useState<string | null>(null);
  const [dateTo, setDateTo]           = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [disposedKey, setDisposedKey] = useState("ALL");

  const businessId = useSelector((s: { auth: IAuth }) => s.auth.user)?.currentBusinessId ?? null;
  const disposedFilter = DISPOSED_FILTER.find((d) => d.value === disposedKey)?.disposed;

  // Reset to page 1 whenever a filter or the bucket changes.
  const resetPage = () => setPage(1);

  // ─── Single-row dispose state ─────────────────────────────────────────────
  const [disposeTarget, setDisposeTarget]       = useState<DisposeTarget | null>(null);
  const [disposalNotes, setDisposalNotes]       = useState("");
  const [finalDisposition, setFinalDisposition] = useState<FinalDisposition>("DESTROYED");
  const [singleBusy, setSingleBusy]             = useState(false);
  const [singleOpened, { open: openSingle, close: closeSingle }] = useDisclosure(false);

  // ─── Bulk action state ────────────────────────────────────────────────────
  // O156 — the selection survives page changes (row objects kept so CSV export covers every page); only a tab change or an action clears it.
  const [selected, setSelected]         = useState<Record<string, IReturnDamagedInventory>>({});
  const selectedIds = Object.keys(selected);
  const [bulkModal, setBulkModal]       = useState<BulkModal>(null);
  const [bulkNotes, setBulkNotes]       = useState("");
  const [bulkDisposition, setBulkDisposition] = useState<FinalDisposition>("DESTROYED");
  const [bulkBusy, setBulkBusy]         = useState(false);

  // Tab counts + analytics widget — one shared turnover call (seller-scoped by the BE).
  const { data: turnover } = useSWR(
    ["damaged-inventory.turnover"],
    () => DamagedInventoryApi.turnover(),
    { revalidateOnFocus: false }
  );
  const bucketStat = (code: InventoryImpactType) => turnover?.buckets.find((b) => b.inventoryImpact === code);

  const firstFilled = BUCKET_TAB_META.find((b) => (bucketStat(b.value)?.activeRows ?? 0) > 0)?.value;
  const activeBucket: InventoryImpactType = bucketChoice ?? firstFilled ?? "RESTOCK_GOOD";

  const { data, error, isLoading, mutate } = useSWR(
    ["damaged-inventory.list", activeBucket, page, sku, dateFrom, dateTo, warehouseId, disposedKey],
    () => DamagedInventoryApi.list({
      inventoryImpact    : activeBucket,
      sku                : sku.trim() || undefined,
      // O157 — a bare YYYY-MM-DD dateTo already includes the whole day (BE, Ulaanbaatar time).
      dateFrom           : dateFrom || undefined,
      dateTo             : dateTo || undefined,
      warehouseLocationId: warehouseId || undefined,
      disposed           : disposedFilter,
      page,
      limit              : PAGE_SIZE
    }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Warehouse filter options (canonical warehouse picker, as in warehouse-receipt).
  const { data: warehouseRows = [] } = useSWR(
    businessId ? ["delivery.warehouse-select", businessId] : null,
    () => DeliveryApi.warehouseSelect({ businessId: businessId as string, limit: 100 }),
    { revalidateOnFocus: false }
  );
  const warehouseOptions = (warehouseRows as Array<{ id: string; name: string }>)
    .map((w) => ({ value: w.id, label: w.name }));

  const totalPages = data?.pagination.totalPages ?? 1;
  const total = data?.pagination.total ?? 0;
  const items = data?.items ?? [];

  const activeItems = items.filter((it) => !it.disposedAt);
  const allSelected = activeItems.length > 0 && activeItems.every((it) => selected[it.id]);
  const someSelected = activeItems.some((it) => selected[it.id]);

  const hasFilter = !!(sku.trim() || dateFrom || dateTo || warehouseId || disposedKey !== "ALL");

  // Selection survives paging (O156) but not a filter change: bulk actions must never
  // send rows the current filter hides.
  useEffect(() => { setSelected({}); }, [sku, dateFrom, dateTo, warehouseId, disposedKey]);

  const overCap = selectedIds.length > RETURN_BULK_MAX;
  const capReason = overCap ? t("inbound:damaged.list.capReason", { max: RETURN_BULK_MAX }) : undefined;

  const onTabChange = (v: string | null) => {
    if (v) {
      setBucketChoice(v as InventoryImpactType);
      setSelected({});
      resetPage();
    }
  };

  const onClearFilters = () => {
    setSku("");
    setDateFrom(null);
    setDateTo(null);
    setWarehouseId(null);
    setDisposedKey("ALL");
    resetPage();
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allSelected) {
        activeItems.forEach((it) => { delete next[it.id]; });
      } else {
        activeItems.forEach((it) => { next[it.id] = it; });
      }
      return next;
    });
  };

  const toggleOne = (it: IReturnDamagedInventory) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[it.id]) delete next[it.id]; else next[it.id] = it;
      return next;
    });
  };

  // ─── Single dispose ───────────────────────────────────────────────────────
  const onDisposeClick = (target: DisposeTarget) => {
    setDisposeTarget(target);
    setDisposalNotes("");
    setFinalDisposition("DESTROYED");
    openSingle();
  };

  const onDisposeConfirm = async () => {
    if (!disposeTarget) return;
    try {
      setSingleBusy(true);
      await DamagedInventoryApi.dispose(disposeTarget.id, {
        finalDisposition,
        disposalNotes: disposalNotes || null
      });
      message.success(t("inbound:damaged.single.disposeSuccess"));
      const disposedId = disposeTarget.id;
      setSelected((prev) => { const next = { ...prev }; delete next[disposedId]; return next; });
      closeSingle();
      mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setSingleBusy(false);
    }
  };

  // ─── Bulk actions ─────────────────────────────────────────────────────────
  const openBulkModal = (mode: BulkModal) => {
    setBulkNotes("");
    setBulkDisposition("DESTROYED");
    setBulkModal(mode);
  };

  const closeBulkModal = () => setBulkModal(null);

  const runBulk = async (call: () => Promise<IBulkResponse>) => {
    try {
      setBulkBusy(true);
      const result = await call();
      showBulkResult(t, result);
      closeBulkModal();
      setSelected({});
      mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkDispose = () => runBulk(() => DamagedInventoryApi.bulkDispose({
    damagedInventoryIds: selectedIds,
    finalDisposition   : bulkDisposition,
    disposalNotes      : bulkNotes || null
  }));

  const onBulkMove = () => runBulk(() => DamagedInventoryApi.bulkMoveBucket({
    damagedInventoryIds: selectedIds,
    inventoryImpact    : "RESTOCK_GOOD",
    notes              : bulkNotes || null
  }));

  // «Зарсан гэж бүртгэх» only records the final disposition RESOLD: no price, order or invoice is created (O159).
  const onSellAsDamaged = () => runBulk(() => DamagedInventoryApi.bulkDispose({
    damagedInventoryIds: selectedIds,
    finalDisposition   : "RESOLD",
    disposalNotes      : bulkNotes || null
  }));

  // §14 — CSV export of the selected damaged-inventory rows (every page). Built FE-side from
  // the loaded rows (no export endpoint); UTF-8 BOM so Excel reads Cyrillic.
  const exportSelectedCsv = () => {
    const rows = Object.values(selected);
    if (rows.length === 0) return;
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      t("inbound:damaged.csv.returnNumber"), "SKU", t("inbound:damaged.csv.bucket"),
      t("inbound:damaged.csv.quantity"), t("inbound:damaged.csv.location"), t("inbound:damaged.csv.damageDesc"),
      t("inbound:damaged.csv.received"), t("inbound:damaged.csv.disposal")
    ];
    const lines = [
      headers.join(","),
      ...rows.map((it) => [
        it.returnNumber ?? "",
        it.sku ?? "",
        bucketLabel(t, it.inventoryImpact),
        it.quantity,
        it.bucketLocationCode ?? "",
        it.damageDescription ?? "",
        it.receivedAt ? dateTimeFormat(it.receivedAt) : "",
        it.disposedAt ? dispositionLabel(t, it.finalDisposition) : t("inbound:damaged.csv.pending")
      ].map(escape).join(","))
    ];
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, `damaged_inventory_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const footer = (
    <>
      <Text span style={TNUM}>
        {items.length ? `${(page - 1) * PAGE_SIZE + 1}-${(page - 1) * PAGE_SIZE + items.length} / ${total}` : `0 / ${total}`}
      </Text>
      {totalPages > 1 ? <Pagination value={page} onChange={setPage} total={totalPages} size="sm" /> : <Box />}
    </>
  );

  return (
    <PageLayout
      title={t("inbound:damaged.list.title")}
      subTitle={t("inbound:damaged.list.subtitle")}
      breadcrumb={[
        { to: "/", label: t("inbound:damaged.breadcrumb.home") },
        { to: "/returns", label: t("inbound:damaged.breadcrumb.returns") },
        { label: t("inbound:damaged.list.title") }
      ]}
      loading={isLoading && !data}
      extra={[
        <Button
          key="turnover"
          component={Link}
          to="/damaged-inventory/turnover"
          variant="default"
          rightSection={<ArrowRightIcon size={16} />}
        >
          {t("inbound:damaged.list.turnoverAnalytics")}
        </Button>
      ]}
    >
      <Stack gap="md">
        {/* ── Analytics widget: per-category turnover ── */}
        <DetailCard
          title={t("inbound:damaged.list.turnoverTitle")}
          extra={<Anchor component={Link} to="/damaged-inventory/turnover" fz="var(--nw-fz-body)" c="dark.9" underline="always">{t("inbound:damaged.list.detail")}</Anchor>}
        >
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            {BUCKET_TAB_META.map((bucket) => {
              const stat = bucketStat(bucket.value);
              return (
                <Stack key={bucket.value} gap={2}>
                  <Group gap={8} wrap="nowrap">
                    <Dot tone={bucket.tone} />
                    <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">{bucketLabel(t, bucket.value)}</Text>
                  </Group>
                  <Text ff="monospace" fz={22} fw={650} style={TNUM}>{t("inbound:damaged.pcs", { count: stat?.activeQuantity ?? 0 })}</Text>
                  <Text fz="var(--nw-fz-meta)" c="var(--nw-text-2)">
                    {stat && stat.activeRows > 0 ? t("inbound:damaged.list.avgDays", { days: stat.avgDaysInBucket }) : "-"}
                  </Text>
                </Stack>
              );
            })}
          </SimpleGrid>
        </DetailCard>

        {/* ── Filter bar ── */}
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput
            label={t("inbound:damaged.list.skuSearch")}
            placeholder="SKU"
            value={sku}
            onChange={(e) => { setSku(e.currentTarget.value); resetPage(); }}
            w={180}
          />
          <DatePickerInput
            label={t("inbound:damaged.list.startDate")}
            placeholder={t("inbound:damaged.list.datePlaceholder")}
            valueFormat="YYYY-MM-DD"
            value={dateFrom}
            onChange={(v) => { setDateFrom(v); resetPage(); }}
            clearable
            w={160}
          />
          <DatePickerInput
            label={t("inbound:damaged.list.endDate")}
            placeholder={t("inbound:damaged.list.datePlaceholder")}
            valueFormat="YYYY-MM-DD"
            value={dateTo}
            onChange={(v) => { setDateTo(v); resetPage(); }}
            clearable
            w={160}
          />
          <Select
            label={t("inbound:damaged.list.warehouse")}
            placeholder={t("inbound:damaged.list.all")}
            data={warehouseOptions}
            value={warehouseId}
            onChange={(v) => { setWarehouseId(v); resetPage(); }}
            clearable
            searchable
            w={200}
          />
          <Select
            label={t("inbound:damaged.list.disposal")}
            data={disposedFilterOptions(t)}
            value={disposedKey}
            onChange={(v) => { setDisposedKey(v ?? "ALL"); resetPage(); }}
            allowDeselect={false}
            w={160}
          />
          <Button variant="subtle" color="gray" onClick={onClearFilters}>{t("inbound:damaged.list.clear")}</Button>
        </Group>

        <Tabs value={activeBucket} onChange={onTabChange} variant="pills" color="gray" radius="md" classNames={{ tab: classes.tabSoft }}>
          <Tabs.List>
            {BUCKET_TAB_META.map((bucket) => {
              const cnt = bucketStat(bucket.value)?.activeRows;
              const label = bucketLabel(t, bucket.value);
              return (
                <Tabs.Tab key={bucket.value} value={bucket.value}>
                  {cnt ? `${label} (${cnt})` : label}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>

        {selectedIds.length > 0 && (
          <SelectionBar label={
            <>
              {t("inbound:damaged.list.rowsSelected", { count: selectedIds.length })}
              {" · "}
              <Text span ff="monospace" style={TNUM}>{t("inbound:damaged.list.selectedCap", { count: selectedIds.length, max: RETURN_BULK_MAX })}</Text>
            </>
          }>
            <DeliveryActionButton state={mutateState} disabledReason={capReason} variant="default" onClick={() => openBulkModal("dispose")}>
              {t("inbound:damaged.list.bulkDispose")}
            </DeliveryActionButton>
            {activeBucket !== "RESTOCK_GOOD" && (
              <DeliveryActionButton state={mutateState} disabledReason={capReason} variant="default" onClick={() => openBulkModal("move")}>
                {t("inbound:damaged.list.moveToRestock")}
              </DeliveryActionButton>
            )}
            <DeliveryActionButton state={mutateState} disabledReason={capReason} variant="default" onClick={() => openBulkModal("sell-damaged")}>
              {t("inbound:damaged.list.sellDamaged")}
            </DeliveryActionButton>
            <Button variant="default" leftSection={<DownloadSimpleIcon size={16} />} onClick={exportSelectedCsv}>
              {t("inbound:damaged.list.downloadCsv")}
            </Button>
            <Anchor component="button" type="button" fz="var(--nw-fz-body)" onClick={() => setSelected({})}>
              {t("inbound:damaged.list.clearSelection")}
            </Anchor>
          </SelectionBar>
        )}

        <TableFrame footer={items.length ? footer : undefined}>
          {error && !data ? (
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          ) : isLoading && !data ? (
            <ListState state="loading" columns={9} />
          ) : items.length === 0 ? (
            hasFilter ? (
              <ListState state="noResults" action={{ onClick: onClearFilters }} />
            ) : (
              <ListState state="empty" title={t("inbound:damaged.list.emptyBucket")} description={t("inbound:damaged.list.emptyHint")} />
            )
          ) : (
            <Table.ScrollContainer minWidth={980}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={44}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={toggleAll}
                        aria-label={t("inbound:damaged.list.selectAll")}
                      />
                    </Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colActions")}</Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colReturn")}</Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colItemSku")}</Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colDamageDesc")}</Table.Th>
                    <Table.Th ta="right">{t("inbound:damaged.list.colQuantity")}</Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colLocation")}</Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colReceived")}</Table.Th>
                    <Table.Th>{t("inbound:damaged.list.colDisposal")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {items.map((it) => (
                    <Table.Tr key={it.id} data-selected={selected[it.id] ? true : undefined}>
                      <Table.Td>
                        {it.disposedAt ? null : (
                          <Checkbox
                            checked={!!selected[it.id]}
                            onChange={() => toggleOne(it)}
                            aria-label={it.returnNumber ?? it.sku ?? t("inbound:damaged.list.selectRow")}
                          />
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <DeliveryRowAction
                            label={t("inbound:damaged.list.rowDetail")}
                            icon={<EyeIcon />}
                            onClick={() => navigate(`/damaged-inventory/${it.id}`)}
                          />
                          {it.disposedAt ? null : (
                            <DeliveryRowAction
                              label={t("inbound:damaged.list.markDisposed")}
                              icon={<CheckCircleIcon />}
                              state={mutateState}
                              onClick={() => onDisposeClick({ id: it.id, bucket: activeBucket })}
                            />
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {it.returnRequestId && it.returnNumber ? (
                          <Anchor component={Link} to={`/returns/${it.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no" style={{ whiteSpace: "nowrap" }}>
                            {it.returnNumber}
                          </Anchor>
                        ) : <Text size="sm">-</Text>}
                      </Table.Td>
                      <Table.Td maw={260}>
                        <Text size="sm" fw={500} lineClamp={1}>{it.productName ?? it.sku ?? "-"}</Text>
                        {it.productName && it.sku ? (
                          <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)" translate="no">{it.sku}</Text>
                        ) : null}
                      </Table.Td>
                      <Table.Td maw={260}>
                        <Text size="sm" lineClamp={2}>{it.damageDescription || "-"}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" ff="monospace" style={TNUM}>{it.quantity}</Text>
                      </Table.Td>
                      <Table.Td><Text size="sm">{it.bucketLocationCode || "-"}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace" style={{ ...TNUM, whiteSpace: "nowrap" }}>{dateFormat(it.receivedAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        {it.disposedAt ? (
                          <Stack gap={0}>
                            <Text size="sm">{dispositionLabel(t, it.finalDisposition)}</Text>
                            <Text size="sm" ff="monospace" c="var(--nw-text-2)" style={TNUM}>{dateFormat(it.disposedAt)}</Text>
                          </Stack>
                        ) : <Text size="sm">-</Text>}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </TableFrame>
      </Stack>

      {/* ── Single dispose modal ──────────────────────────────────────────── */}
      <Modal opened={singleOpened} onClose={closeSingle} title={t("inbound:damaged.single.disposeTitle")} size={440}>
        <Stack gap="md">
          <Select
            label={t("inbound:damaged.list.dispositionLabel")}
            data={dispositionOptions(t)}
            value={finalDisposition}
            onChange={(v) => v && setFinalDisposition(v as FinalDisposition)}
            allowDeselect={false}
            required
          />
          <Textarea
            label={t("inbound:damaged.single.notes")}
            value={disposalNotes}
            onChange={(e) => setDisposalNotes(e.currentTarget.value)}
            autosize minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeSingle}>{t("inbound:damaged.single.close")}</Button>
            <Button onClick={onDisposeConfirm} loading={singleBusy}>{t("inbound:damaged.single.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Bulk dispose modal ────────────────────────────────────────────── */}
      <Modal
        opened={bulkModal === "dispose"}
        onClose={closeBulkModal}
        title={t("inbound:damaged.bulk.disposeTitle", { count: selectedIds.length })}
        size={440}
      >
        <Stack gap="md">
          <Select
            label={t("inbound:damaged.list.dispositionLabel")}
            data={dispositionOptions(t)}
            value={bulkDisposition}
            onChange={(v) => v && setBulkDisposition(v as FinalDisposition)}
            allowDeselect={false}
            required
          />
          <Textarea
            label={t("inbound:damaged.single.notes")}
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.currentTarget.value)}
            autosize minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeBulkModal}>{t("inbound:damaged.bulk.close")}</Button>
            <Button onClick={onBulkDispose} loading={bulkBusy}>{t("inbound:damaged.bulk.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Bulk category change (to RESTOCK_GOOD) modal ─────────────────── */}
      <Modal
        opened={bulkModal === "move"}
        onClose={closeBulkModal}
        title={t("inbound:damaged.bulk.moveTitle")}
        size={440}
      >
        <Stack gap="md">
          <Text size="sm" c="var(--nw-text-2)">
            {t("inbound:damaged.bulk.moveBody", { count: selectedIds.length })}
          </Text>
          <Textarea
            label={t("inbound:damaged.bulk.notesOptional")}
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.currentTarget.value)}
            autosize minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeBulkModal}>{t("inbound:damaged.bulk.close")}</Button>
            <Button onClick={onBulkMove} loading={bulkBusy}>{t("inbound:damaged.bulk.move")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Record as sold modal ──────────────────────────────────────────── */}
      <Modal
        opened={bulkModal === "sell-damaged"}
        onClose={closeBulkModal}
        title={t("inbound:damaged.bulk.sellTitle", { count: selectedIds.length })}
        size={440}
      >
        <Stack gap="md">
          <Text size="sm" c="var(--nw-text-2)">
            {t("inbound:damaged.bulk.sellBody", { disposition: dispositionLabel(t, "RESOLD") })}
          </Text>
          <Textarea
            label={t("inbound:damaged.bulk.notesOptional")}
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.currentTarget.value)}
            autosize minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeBulkModal}>{t("inbound:damaged.bulk.close")}</Button>
            <Button onClick={onSellAsDamaged} loading={bulkBusy}>{t("inbound:damaged.bulk.sell")}</Button>
          </Group>
        </Stack>
      </Modal>
    </PageLayout>
  );
}
